import { type ComponentPropsWithoutRef, useEffect, useState } from 'react';
import Modal from '@/components/Modal';
import { BlockButton } from '@/components/BlockButton';
import { Textarea } from '@/components/Textarea';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import { useBulkComment } from '../api/useBulkJobPostEndpointActions';

type Props = {
  modal: Omit<ComponentPropsWithoutRef<typeof Modal>, 'children'>;
  close: () => void;
  title: string;
  initialComment: string;
  jobPostId: number;
  candidateId: number;
  onSaved: (comment: string) => void;
};

export default function SuggestedCandidateCommentModal({
  modal,
  close,
  title,
  initialComment,
  jobPostId,
  candidateId,
  onSaved,
}: Props) {
  const [comment, setComment] = useState(initialComment);
  const { mutateAsync: bulkComment, isPending } = useBulkComment();

  useEffect(() => {
    if (modal.isShow) setComment(initialComment);
  }, [modal.isShow, initialComment]);

  const handleSave = async () => {
    if (isPending) return;
    try {
      await bulkComment({ jobPostId, candidateIds: [candidateId], comment });
      onSaved(comment);
      showSuccessToast('エージェントコメントを更新しました');
      close();
    } catch (error) {
      showErrorToast('エージェントコメントの更新に失敗しました');
    }
  };

  return (
    <Modal {...modal}>
      <div className="w-[850px] rounded bg-white">
        <div className="flex h-[56px] items-center border-b border-solid border-border px-6">
          <p className="line-clamp-1 text-base font-bold">{title}</p>
        </div>
        <div className="p-6">
          <Textarea
            value={comment}
            placeholder="エージェントからのコメント"
            className="h-[216px] text-sm leading-[170%]"
            autoFocus
            onChange={(event) => setComment(event.currentTarget.value)}
          />
        </div>
        <div className="flex h-[74px] items-center justify-end gap-4 px-6">
          <div className="w-[94px]">
            <BlockButton type="button" text="キャンセル" colorType="white" onClick={close} />
          </div>
          <div className="w-[80px]">
            <BlockButton
              type="button"
              disabled={isPending || comment === initialComment}
              text={isPending ? '送信中…' : '保存'}
              colorType="blue"
              onClick={handleSave}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
